import { ApiError } from "@/lib/api-client";

// Story 15.7 — the ONE mapper every refusal on Screen 10 reaches the operator
// through. It lives in `routes/admin/` rather than beside the hook, which is the
// shipped split: `admin-orgs-error.ts` and `admin-orgs-derivations.ts` sit here
// while `hooks/queries/` holds only the hook and its PURE types.
//
// Follows the `admin-orgs-error.ts` convention: branch on `ApiError.code` /
// `ApiError.status`, render the server's OWN code verbatim beside the copy, and
// NEVER branch on the server's English `msg` — prose is not an interface, and a
// copy edit on the backend must not silently re-route a banner here.
//
// This surface is a READ and nothing else, so it carries no field errors and no
// coded branches: the console endpoint allocates no error code of its own. What
// it has instead is the shared refusal shape and a default that quotes whatever
// code the server did send. A code this file does not know is surfaced, never
// explained away.

/** Banner descriptor. `variant` maps 1:1 onto a `Banner` variant. */
export interface AdminConsoleBanner {
  variant: "info" | "warn" | "danger";
  title: string;
  body: string;
  /** The server's own error code, rendered verbatim beside the copy. */
  code?: string;
}

const GENERIC: AdminConsoleBanner = {
  variant: "danger",
  title: "The console could not be loaded",
  body: "That read could not be completed. Please try again.",
};

// 401 and an UNCODED 403 are one user-visible situation — the admin surfaces
// answer an anonymous caller with 403 rather than 401, so keying "signed out"
// off 401 alone would report an expired session as a permanent capability
// refusal. `requires_capability` raises `InsufficientPermissionsException`,
// which ships with no `error_code`, so `api-client` fills the literal `UNKNOWN`
// and the two are indistinguishable on the wire.
//
// "Sign in again" is the honest remedy for both, and the route guard already
// keeps a non-super_admin off this screen — so an org_admin who reaches this
// branch got here by holding a session that changed underneath them.
const SESSION_EXPIRED: AdminConsoleBanner = {
  variant: "warn",
  title: "Your session may have expired",
  body: "Sign in again and retry this action.",
};

/**
 * True when the SERVER named the failure. `UNKNOWN` is `api-client`'s
 * placeholder for a response that carried no code at all, so it is not a code.
 */
function hasServerCode(error: ApiError): boolean {
  return Boolean(error.code) && error.code !== "UNKNOWN";
}

/** Map any console read failure to the one banner the screen renders. */
export function toAdminConsoleError(error: unknown): AdminConsoleBanner {
  if (error instanceof ApiError) {
    if (error.status === 401 || (error.status === 403 && !hasServerCode(error))) {
      return SESSION_EXPIRED;
    }
    // Surface the server's OWN code verbatim. No translation table, so a code
    // this file does not own cannot be mis-explained. Status 0 is a transport
    // failure whose "code" is the client's own placeholder, and quoting it would
    // send an operator to support with a string the server never sent.
    if (error.status !== 0 && hasServerCode(error)) {
      return { ...GENERIC, code: error.code };
    }
  }
  // NETWORK_ERROR / AuthExpiredError / unknown — nothing internal leaked.
  return GENERIC;
}
